import React, { useContext, useMemo, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { apiList, baseUrl } from "../../../helpers/apiList";
import { axiosGetApi, axiosPostApi } from "../../../helpers/interceptorServices";
import CustomAlert from "../../../helpers/CustomAlert";
import AadhaarInput from "../../../components/common/AadhaarInput";
import { BankNameContext } from "../../../context/BankNameContext";
import {
  GSTIN_FORMAT,
  MAX_DOC_KB,
  grid,
  inputClass,
  addressError,
  celebrate,
  fileError,
  Field,
  DocumentTile,
  ReviewSections,
  WizardShell,
} from "../../../components/common/OnboardingWizard";

// Step-by-step franchise onboarding (T10), the franchise twin of the Add LMBP
// flow. Replaces AddFranchiseTabs for "Add Franchise" only; edit / view /
// authorise keep the existing form. It sends the same request as that form:
// the "request" JSON built from the same keys (Aadhaar masked for display plus
// aadharNumberRaw, bank fields empty unless the optional bank step is filled),
// plus the seven document parts, to /master/franchise/create.

const STEPS = [
  { key: "business", title: "Business", hint: "Start with the GST number" },
  { key: "contact", title: "Contact & address", hint: "Who we talk to, and where" },
  { key: "bank", title: "Bank", hint: "Optional · for settlements" },
  { key: "kyc", title: "KYC & documents", hint: "ID numbers and uploads" },
  { key: "review", title: "Review", hint: "Check and submit" },
];

const FRANCHISE_TYPES = ["Proprietor", "Private Limited", "Individual", "LLP", "Public Limited"];
// Types where the franchise PAN belongs to a company, so the signatory's PAN must differ
const COMPANY_TYPES = ["Private Limited", "Public Limited", "LLP"];

// Payload key → multipart part name, as the old upload step sends them
const DOCUMENTS = [
  { key: "gstCertificate", part: "gstCertificate", label: "GST Certificate" },
  { key: "incorporationCertificate", part: "incorporationCertificate", label: "Franchise Incorporation Certificate" },
  { key: "aadharFront", part: "aadharFront", label: "Aadhaar front" },
  { key: "aadharBack", part: "aadharBack", label: "Aadhaar back" },
  { key: "pan", part: "panFile", label: "Franchise PAN card" },
  { key: "authPersonPan", part: "authPersonPanFile", label: "Authorised person PAN card" },
  { key: "boardResolution", part: "boardResolution", label: "Board Resolution" },
];

const emptyPayload = (username) => ({
  username,
  franchiseName: "",
  emailId: "",
  addr1: "",
  addr2: "",
  addr3: "",
  city: "",
  state: "",
  pinCode: "",
  primaryContactName: "",
  primaryContactMobile: "",
  bankName: "",
  accountNo: "",
  ifscCode: "",
  gstNumber: "",
  aadharNumber: "",
  panNumber: "",
  companyPanNumber: "",
  franchiseType: "",
});

const hasBank = (p) => Boolean(p.bankName || p.accountNo || p.ifscCode || p.confirmAccountNo);

// ---------------- field rules (same rules and messages as the old form) ----------------

const RULES = {
  gstNumber: (p) => {
    if (!p.gstNumber) return "GST Number is required";
    if (!GSTIN_FORMAT.test(p.gstNumber)) return "Invalid GST Number format";
    return null;
  },
  franchiseName: (p) => {
    const v = (p.franchiseName || "").trim();
    if (!v) return "Franchise Name is required";
    if (!/^[A-Za-z0-9@&.,'\- ]+$/.test(v) || v.length < 2 || v.length > 50)
      return "Franchise name must be valid and between 2 and 50 characters";
    if (/^(.)\1{2,}$/.test(v)) return "Franchise name cannot be made of a single repeated character";
    return null;
  },
  emailId: (p) => {
    const v = p.emailId || "";
    if (!v) return "Email is required";
    if (
      !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+\.(com|in|pe|co\.[a-zA-Z]{2})$/.test(v) ||
      /\.com\./.test(v) ||
      /\.co\.[a-zA-Z]{2}\./.test(v)
    )
      return "Enter valid email ending with .com .co.xx or .in";
    return null;
  },
  franchiseType: (p) => (p.franchiseType ? null : "Franchise Type is required"),
  primaryContactName: (p) => {
    const v = (p.primaryContactName || "").trim();
    if (!v) return "Contact person name is required";
    if (!/^[A-Za-z@&.,'\- ]+$/.test(v) || v.length < 2 || v.length > 50)
      return "Contact person name must be between 2 and 50 characters and contain only letters";
    if (!/^[A-Za-z][A-Za-z .-]*[A-Za-z]$/.test(v))
      return "Contact person name must contain only letters, spaces, dots, or hyphens and cannot start or end with spaces or symbols";
    if (/^([A-Za-z])\1+$/.test(v.replace(/[ .-]/g, "")))
      return "Contact person name cannot consist of the same letter repeated";
    return null;
  },
  primaryContactMobile: (p) => {
    const v = p.primaryContactMobile || "";
    if (!v) return "Contact person mobile number is required";
    if (!/^[789]\d{9}$/.test(v)) return "Mobile number must be 10 digits and start with 7, 8, or 9";
    if (/^(\d)\1{9}$/.test(v)) return "Mobile number cannot have all repeated digits";
    if (v === "1234567890" || v === "9876543210") return "Mobile number cannot be a simple sequence";
    return null;
  },
  pinCode: (p) => {
    if (!p.pinCode) return "Pincode is required";
    if (!/^[1-9]\d{5}$/.test(p.pinCode)) return "Pincode must be 6 digits and must not start with 0";
    return null;
  },
  addr1: (p) => addressError(p.addr1, 1, true),
  addr2: (p) => addressError(p.addr2, 2, true),
  addr3: (p) => addressError(p.addr3, 3, false),
  // Bank details are optional (the old form never asked); once started, all three are needed
  bankName: (p) => (hasBank(p) && !p.bankName ? "Bank name is required" : null),
  accountNo: (p) => {
    if (!hasBank(p)) return null;
    if (!p.accountNo) return "Account number is required";
    if (!/^\d{9,18}$/.test(p.accountNo)) return "Account number must be 9 to 18 digits";
    if (/^(\d)\1+$/.test(p.accountNo)) return "Account number cannot have all repeated digits";
    return null;
  },
  confirmAccountNo: (p) =>
    hasBank(p) && p.accountNo && p.confirmAccountNo !== p.accountNo ? "Account numbers do not match" : null,
  ifscCode: (p) => {
    if (!hasBank(p)) return null;
    if (!p.ifscCode) return "IFSC code is required";
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(p.ifscCode)) return "IFSC must be 4 letters, a 0, then 6 letters or digits";
    return null;
  },
  panNumber: (p) => {
    const v = (p.panNumber || "").trim();
    if (!v) return "PAN Number is required";
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(v)) return "Invalid PAN Number format";
    if (COMPANY_TYPES.includes(p.franchiseType) && v === p.companyPanNumber)
      return "Company PAN and Authorised PAN must be different";
    return null;
  },
  aadharNumber: (p) => {
    const raw = (p.aadharNumberRaw || "").trim();
    if (!raw) return "Aadhar Number is required";
    if (!/^[2-9]\d{11}$/.test(raw)) return "Aadhar Number must be exactly 12 digits and start with 2-9";
    if (/^(\d)\1{11}$/.test(raw)) return "Aadhar Number cannot have all repeated digits";
    return null;
  },
};

const STEP_FIELDS = {
  business: ["gstNumber", "franchiseName", "emailId", "franchiseType"],
  contact: ["primaryContactName", "primaryContactMobile", "pinCode", "addr1", "addr2", "addr3"],
  bank: ["bankName", "accountNo", "confirmAccountNo", "ifscCode"],
  kyc: ["panNumber", "aadharNumber"],
  review: [],
};

// Which step a backend error belongs to, so the user lands where they can fix it.
// "Franchise PAN number already exists" is about the authorised person's PAN.
const stepForServerMessage = (msg = "") => {
  const m = msg.toLowerCase();
  if (m.includes("mobile")) return 1;
  if (m.includes("aadhaar") || m.includes("aadhar") || m.includes("pan")) return 3;
  if (m.includes("email") || m.includes("gst")) return 0;
  if (m.includes("bank") || m.includes("ifsc") || m.includes("account")) return 2;
  return null;
};

// ---------------- wizard ----------------

const FranchiseOnboardingWizard = ({ onClose, onCreated }) => {
  const { user } = useSelector((state) => state.auth);
  const username = user?.data?.username;
  const { bankNames } = useContext(BankNameContext) || {};

  const [step, setStep] = useState(0);
  const [p, setP] = useState(() => emptyPayload(username));
  const [files, setFiles] = useState({});
  const [fileErrors, setFileErrors] = useState({});
  const [missingDocs, setMissingDocs] = useState(false);
  const [errors, setErrors] = useState({});
  const [gst, setGst] = useState(null); // lookup result for the current GSTIN
  const [gstChecking, setGstChecking] = useState(false);
  const [pinState, setPinState] = useState(""); // "", "loading", "ok", "error"
  const [submitting, setSubmitting] = useState(false);

  const set = (changes) => setP((prev) => ({ ...prev, ...changes }));
  const clearError = (...keys) =>
    setErrors((prev) => {
      const next = { ...prev };
      keys.forEach((k) => delete next[k]);
      return next;
    });
  const checkField = (key) => {
    const msg = RULES[key]?.(p);
    setErrors((prev) => {
      const next = { ...prev };
      if (msg) next[key] = msg;
      else delete next[key];
      return next;
    });
  };

  // ---- GST: derive PAN and state, check it is new as soon as 15 chars are in ----
  const onGstChange = (raw) => {
    const value = raw.toUpperCase().replace(/[^0-9A-Z]/g, "").slice(0, 15);
    set({ gstNumber: value, companyPanNumber: value.length === 15 ? value.slice(2, 12) : "" });
    clearError("gstNumber");
    setGst(null);
    if (value.length === 15) lookupGst(value);
  };

  const lookupGst = async (value) => {
    if (!GSTIN_FORMAT.test(value)) {
      setErrors((e) => ({ ...e, gstNumber: "Invalid GST Number format" }));
      return;
    }
    setGstChecking(true);
    const pan = value.slice(2, 12);
    try {
      // GSTIN check digit, PAN and state (shared with LMBP onboarding), plus the
      // franchise duplicate checks the old form's two "Check" buttons ran
      const [lookup, gstStatus, panStatus] = await Promise.all([
        axiosGetApi(`${apiList.GST_LOOKUP}/${value}`).catch(() => null),
        axiosGetApi(`${apiList.GET_FRANCHISE_GST_STATUS}/${value}`).catch(() => null),
        axiosGetApi(`${apiList.GET_FRANCHISE_COMPANY_PAN_STATUS}/${pan}`).catch(() => null),
      ]);
      const l = lookup?.data;
      if (l && l.respCode !== "00") {
        setErrors((e) => ({ ...e, gstNumber: l.message || "Invalid GST Number" }));
        return;
      }
      // "00" = already registered, "01" = new (franchise status endpoints)
      const gstCode = gstStatus?.data?.respCode;
      const panCode = panStatus?.data?.respCode;
      let err = null;
      if (gstCode === "00") err = "This GST number is already registered with another franchise.";
      else if (panCode === "00") err = "A franchise with this PAN is already registered.";
      else if (!l || gstCode !== "01" || panCode !== "01") err = "Could not check the GST number. Please try again.";
      if (err) setErrors((e) => ({ ...e, gstNumber: err }));
      else setGst(l.data);
      // Registered details, when a GST data service is configured
      const det = l?.data?.details;
      if (det) {
        setP((prev) => ({
          ...prev,
          franchiseName: prev.franchiseName || (det.tradeName || det.name || "").toUpperCase().slice(0, 50),
          addr1: prev.addr1 || det.address1 || "",
          addr2: prev.addr2 || det.address2 || "",
          addr3: prev.addr3 || det.address3 || "",
          pinCode: prev.pinCode || det.pinCode || "",
        }));
        if (det.pinCode && !p.pinCode) lookupPincode(det.pinCode);
      }
    } finally {
      setGstChecking(false);
    }
  };

  // ---- pincode → city / state (same lookup as before) ----
  const lookupPincode = (pin) => {
    if (!/^\d{6}$/.test(pin)) return;
    setPinState("loading");
    axios
      .get(`${baseUrl}/sms/pincode/${pin}`)
      .then((r) => {
        const { status, message, data } = r.data || {};
        if (!status || !data || data.length === 0) {
          set({ city: "", state: "" });
          setPinState("error");
          setErrors((e) => ({ ...e, pinCode: message || "Enter a valid Pincode" }));
        } else {
          set({ city: data[0].cityName, state: data[0].state });
          setPinState("ok");
          clearError("pinCode");
        }
      })
      .catch(() => {
        setPinState("error");
        setErrors((e) => ({ ...e, pinCode: "Error fetching pincode data" }));
      });
  };

  // ---- documents ----
  const onFile = (key, file) => {
    if (!file) return;
    const err = fileError(file);
    setFileErrors((e) => ({ ...e, [key]: err }));
    if (!err) setFiles((f) => ({ ...f, [key]: file }));
  };
  const removeFile = (key) =>
    setFiles((f) => {
      const next = { ...f };
      delete next[key];
      return next;
    });
  const allDocsIn = DOCUMENTS.every((d) => files[d.key]);

  // ---- navigation ----
  const validateStep = (index) => {
    const found = {};
    STEP_FIELDS[STEPS[index].key].forEach((k) => {
      const msg = RULES[k]?.(p);
      if (msg) found[k] = msg;
    });
    // A duplicate or failed check found by the lookup blocks the step too
    if (index === 0 && errors.gstNumber && !found.gstNumber) found.gstNumber = errors.gstNumber;
    if (index === 0 && !found.gstNumber && !gst) found.gstNumber = "The GST number has not been checked yet.";
    if (index === 3 && !allDocsIn) found.documents = "All documents are required";
    return found;
  };

  const showStepErrors = (found) => {
    setErrors((prev) => ({ ...prev, ...found }));
    if (found.documents) setMissingDocs(true);
    setTimeout(
      () => document.querySelector(".franchise-wizard .error")?.scrollIntoView({ behavior: "smooth", block: "center" }),
      50,
    );
  };

  const next = () => {
    const found = validateStep(step);
    if (Object.keys(found).length) return showStepErrors(found);
    setStep((s) => s + 1);
  };

  const goTo = (index) => {
    // Free movement backwards; forwards only through valid steps
    if (index <= step) return setStep(index);
    for (let i = step; i < index; i++) {
      const found = validateStep(i);
      if (Object.keys(found).length) {
        setStep(i);
        showStepErrors(found);
        return;
      }
    }
    setStep(index);
  };

  const submit = async () => {
    for (let i = 0; i < STEPS.length - 1; i++) {
      const found = validateStep(i);
      if (Object.keys(found).length) {
        setStep(i);
        showStepErrors(found);
        return;
      }
    }
    // Same body as the previous form: the payload with trimmed text fields
    // (confirmAccountNo is only for the screen)
    const { confirmAccountNo, ...rest } = p;
    const request = {
      ...rest,
      franchiseName: p.franchiseName.trim(),
      addr1: p.addr1.trim(),
      addr2: p.addr2.trim(),
      addr3: p.addr3.trim(),
      primaryContactName: p.primaryContactName.trim(),
    };
    const formData = new FormData();
    formData.append("request", JSON.stringify(request));
    DOCUMENTS.forEach((d) => files[d.key] && formData.append(d.part, files[d.key]));

    setSubmitting(true);
    try {
      const r = await axiosPostApi(apiList.CREATE_FRANCHISE_MASTER, formData);
      if (r?.status === 200 && r?.data?.status === true) {
        celebrate();
        await CustomAlert({
          title: "Success",
          text: r.data.message || "Franchise added successfully",
          icon: "success",
          confirmButtonText: "Ok",
        });
        onCreated && onCreated();
        onClose();
      } else {
        const msg = r?.data?.message || "There was an issue processing the request.";
        const back = stepForServerMessage(msg);
        CustomAlert({ title: "Could not create franchise", text: msg, icon: "error", confirmButtonText: "OK" });
        if (back !== null) setStep(back);
      }
    } catch (e) {
      CustomAlert({ title: "Error", text: "An error occurred while processing.", icon: "error", confirmButtonText: "Retry" });
    } finally {
      setSubmitting(false);
    }
  };

  const gstStateMismatch =
    gst?.state && p.state && pinState === "ok" && gst.state.toUpperCase() !== String(p.state).toUpperCase();

  // ---------------- steps ----------------

  const renderBusiness = () => (
    <div style={grid}>
      <Field
        label="GST number"
        required
        error={errors.gstNumber}
        hint={
          gstChecking
            ? "Checking…"
            : gst
              ? `Valid GSTIN · ${gst.state || "state " + gst.stateCode} · not registered with a franchise`
              : "Enter it first: the franchise PAN and state are filled from it."
        }
        full
      >
        <input
          className={inputClass(errors.gstNumber)}
          value={p.gstNumber}
          placeholder="e.g. 27ABCDE1234F1Z5"
          maxLength={15}
          autoFocus
          onChange={(e) => onGstChange(e.target.value)}
          onBlur={() => p.gstNumber.length !== 15 && checkField("gstNumber")}
          style={{ fontFamily: "monospace", letterSpacing: "1px", maxWidth: "360px" }}
        />
      </Field>
      <Field label="Franchise PAN" hint={p.companyPanNumber ? "From the GST number" : "Filled from the GST number"}>
        <input className={inputClass(null, true)} value={p.companyPanNumber} readOnly placeholder="—" />
      </Field>
      <Field label="Franchise name" required error={errors.franchiseName}>
        <input
          className={inputClass(errors.franchiseName)}
          value={p.franchiseName}
          placeholder="Business name"
          onChange={(e) => {
            set({ franchiseName: e.target.value.toUpperCase() });
            clearError("franchiseName");
          }}
          onBlur={() => checkField("franchiseName")}
        />
      </Field>
      <Field label="Email" required error={errors.emailId}>
        <input
          type="email"
          className={inputClass(errors.emailId)}
          value={p.emailId}
          placeholder="name@business.com"
          onChange={(e) => {
            set({ emailId: e.target.value.toLowerCase().trim() });
            clearError("emailId");
          }}
          onBlur={() => checkField("emailId")}
        />
      </Field>
      <Field label="Franchise type" required error={errors.franchiseType}>
        <select
          className={inputClass(errors.franchiseType)}
          value={p.franchiseType}
          onChange={(e) => {
            set({ franchiseType: e.target.value });
            clearError("franchiseType", "panNumber");
          }}
        >
          <option value="">Select type</option>
          {FRANCHISE_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </Field>
    </div>
  );

  const renderContact = () => (
    <div style={grid}>
      <Field label="Contact person" required error={errors.primaryContactName}>
        <input
          className={inputClass(errors.primaryContactName)}
          value={p.primaryContactName}
          maxLength={50}
          placeholder="Full name"
          onChange={(e) => {
            if (/^[A-Za-z .-]*$/.test(e.target.value)) {
              set({ primaryContactName: e.target.value });
              clearError("primaryContactName");
            }
          }}
          onBlur={() => checkField("primaryContactName")}
        />
      </Field>
      <Field label="Mobile number" required error={errors.primaryContactMobile}>
        <input
          className={inputClass(errors.primaryContactMobile)}
          value={p.primaryContactMobile}
          inputMode="numeric"
          maxLength={10}
          placeholder="10-digit mobile"
          onChange={(e) => {
            const v = e.target.value.replace(/\D/g, "").slice(0, 10);
            set({ primaryContactMobile: v });
            clearError("primaryContactMobile");
          }}
          onBlur={() => checkField("primaryContactMobile")}
        />
      </Field>
      <Field
        label="Pincode"
        required
        error={errors.pinCode}
        hint={
          pinState === "loading"
            ? "Looking up…"
            : pinState === "ok"
              ? `${p.city}, ${p.state}`
              : "City and state are filled from it"
        }
      >
        <input
          className={inputClass(errors.pinCode)}
          value={p.pinCode}
          inputMode="numeric"
          maxLength={6}
          placeholder="6-digit pincode"
          onChange={(e) => {
            const v = e.target.value.replace(/\D/g, "").slice(0, 6);
            set({ pinCode: v });
            clearError("pinCode");
            if (v.length === 6) lookupPincode(v);
            else setPinState("");
          }}
          onBlur={() => checkField("pinCode")}
        />
      </Field>
      <Field label="City / State">
        <input className={inputClass(null, true)} readOnly value={p.city ? `${p.city}, ${p.state}` : ""} placeholder="—" />
        {gstStateMismatch && (
          <div style={{ color: "#b45309", fontSize: "12px", marginTop: "4px" }}>
            The GST number is registered in {gst.state}; this pincode is in {p.state}.
          </div>
        )}
      </Field>
      {[1, 2, 3].map((n) => (
        <Field key={n} label={`Address line ${n}`} required={n < 3} error={errors[`addr${n}`]} full={n === 1}>
          <input
            className={inputClass(errors[`addr${n}`])}
            value={p[`addr${n}`]}
            maxLength={150}
            placeholder={n === 1 ? "Shop / building, street" : n === 2 ? "Area, landmark" : "Optional"}
            onChange={(e) => {
              set({ [`addr${n}`]: e.target.value });
              clearError(`addr${n}`);
            }}
            onBlur={() => checkField(`addr${n}`)}
          />
        </Field>
      ))}
    </div>
  );

  const bankFields = ["bankName", "accountNo", "confirmAccountNo", "ifscCode"];
  const renderBank = () => (
    <>
      <p style={{ color: "#6b7280", fontSize: "14px", marginTop: 0 }}>
        Optional. Leave all fields empty to add the bank account later.
      </p>
      <div style={grid}>
        <Field label="Bank" required={hasBank(p)} error={errors.bankName}>
          <select
            className={inputClass(errors.bankName)}
            value={p.bankName}
            onChange={(e) => {
              set({ bankName: e.target.value });
              clearError(...bankFields);
            }}
          >
            <option value="">Select bank</option>
            {(bankNames || []).map((b) => (
              <option key={b.bankName} value={b.bankName}>
                {b.bankName}
              </option>
            ))}
          </select>
        </Field>
        <Field label="IFSC code" required={hasBank(p)} error={errors.ifscCode} hint="e.g. SBIN0001234">
          <input
            className={inputClass(errors.ifscCode)}
            value={p.ifscCode}
            maxLength={11}
            placeholder="11 characters"
            style={{ fontFamily: "monospace", letterSpacing: "1px" }}
            onChange={(e) => {
              set({ ifscCode: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 11) });
              clearError(...bankFields);
            }}
            onBlur={() => checkField("ifscCode")}
          />
        </Field>
        <Field label="Account number" required={hasBank(p)} error={errors.accountNo}>
          <input
            className={inputClass(errors.accountNo)}
            value={p.accountNo}
            inputMode="numeric"
            maxLength={18}
            placeholder="9 to 18 digits"
            onChange={(e) => {
              set({ accountNo: e.target.value.replace(/\D/g, "").slice(0, 18) });
              clearError(...bankFields);
            }}
            onBlur={() => checkField("accountNo")}
          />
        </Field>
        <Field label="Re-enter account number" required={hasBank(p)} error={errors.confirmAccountNo}>
          <input
            className={inputClass(errors.confirmAccountNo)}
            value={p.confirmAccountNo || ""}
            inputMode="numeric"
            maxLength={18}
            placeholder="Same as above"
            onPaste={(e) => e.preventDefault()}
            onChange={(e) => {
              set({ confirmAccountNo: e.target.value.replace(/\D/g, "").slice(0, 18) });
              clearError("confirmAccountNo");
            }}
            onBlur={() => checkField("confirmAccountNo")}
          />
        </Field>
      </div>
    </>
  );

  const renderKyc = () => (
    <>
      <div style={grid}>
        <Field
          label="Authorised person's PAN"
          required
          error={errors.panNumber}
          hint={COMPANY_TYPES.includes(p.franchiseType) ? "Must differ from the franchise PAN" : undefined}
        >
          <input
            className={inputClass(errors.panNumber)}
            value={p.panNumber}
            maxLength={10}
            placeholder="ABCDE1234F"
            style={{ fontFamily: "monospace", letterSpacing: "1px" }}
            onChange={(e) => {
              set({ panNumber: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10) });
              clearError("panNumber");
            }}
            onBlur={() => checkField("panNumber")}
          />
        </Field>
        <Field label="Aadhaar number" required error={errors.aadharNumber}>
          <AadhaarInput
            value={p.aadharNumber}
            error={errors.aadharNumber}
            onChange={(masked, raw) => {
              set({ aadharNumber: masked, aadharNumberRaw: raw });
              clearError("aadharNumber");
            }}
            onBlur={() => checkField("aadharNumber")}
          />
        </Field>
      </div>

      <div style={{ marginTop: "22px" }}>
        <div className="font-medium" style={{ marginBottom: "4px" }}>
          Documents <span style={{ color: "#6b7280", fontWeight: 400, fontSize: "13px" }}>· all required · PDF or image, up to {MAX_DOC_KB} KB each</span>
        </div>
        {missingDocs && !allDocsIn && (
          <div className="error" style={{ color: "#dc2626", fontSize: "12px", marginBottom: "6px" }}>
            Upload all {DOCUMENTS.length} documents to continue ({DOCUMENTS.filter((d) => files[d.key]).length} of {DOCUMENTS.length} done).
          </div>
        )}
        <div style={{ ...grid, gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "12px" }}>
          {DOCUMENTS.map((d) => (
            <DocumentTile
              key={d.key}
              label={d.label}
              required
              missing={missingDocs}
              file={files[d.key]}
              error={fileErrors[d.key]}
              onFile={(f) => onFile(d.key, f)}
              onRemove={() => removeFile(d.key)}
            />
          ))}
        </div>
      </div>
    </>
  );

  const reviewSections = useMemo(
    () => [
      {
        step: 0,
        title: "Business",
        rows: [
          ["GST number", p.gstNumber],
          ["Franchise PAN", p.companyPanNumber || "—"],
          ["Name", p.franchiseName],
          ["Email", p.emailId],
          ["Franchise type", p.franchiseType],
        ],
      },
      {
        step: 1,
        title: "Contact & address",
        rows: [
          ["Contact person", p.primaryContactName],
          ["Mobile", p.primaryContactMobile],
          ["Address", [p.addr1, p.addr2, p.addr3].filter(Boolean).join(", ")],
          ["City / State / PIN", [p.city, p.state, p.pinCode].filter(Boolean).join(", ")],
        ],
      },
      {
        step: 2,
        title: "Bank",
        rows: hasBank(p)
          ? [
              ["Bank", p.bankName],
              ["Account number", p.accountNo ? `•••• ${p.accountNo.slice(-4)}` : ""],
              ["IFSC", p.ifscCode],
            ]
          : [["Bank account", "Not given (can be added later)"]],
      },
      {
        step: 3,
        title: "KYC & documents",
        rows: [
          ["Authorised PAN", p.panNumber],
          ["Aadhaar", p.aadharNumber],
          ["Documents", `${DOCUMENTS.filter((d) => files[d.key]).length} of ${DOCUMENTS.length} uploaded`],
        ],
      },
    ],
    [p, files],
  );

  const renderReview = () => <ReviewSections sections={reviewSections} onEdit={setStep} />;

  const body = [renderBusiness, renderContact, renderBank, renderKyc, renderReview][step]();

  return (
    <WizardShell
      className="franchise-wizard"
      title="Add Franchise"
      steps={STEPS}
      step={step}
      onStepClick={goTo}
      onClose={onClose}
      onBack={() => setStep(step - 1)}
      onNext={next}
      onSubmit={submit}
      nextDisabled={gstChecking}
      submitting={submitting}
      submitLabel="Create franchise"
      submittingLabel="Creating…"
    >
      {body}
    </WizardShell>
  );
};

export default FranchiseOnboardingWizard;
